import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../supabaseClient";

export default function Movie() {
  const { movieId } = useParams();

  const [loading, setLoading] = useState(true);
  const [movie, setMovie] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    loadMovie();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [movieId]);

  async function loadMovie() {
    setLoading(true);
    setError("");

    try {
      const { data, error: movieErr } = await supabase
        .from("movies")
        .select("*")
        .eq("id", movieId)
        .single();

      if (movieErr) throw movieErr;
      setMovie(data);
    } catch (e) {
      setError(e.message || "Failed to load movie.");
    } finally {
      setLoading(false);
    }
  }

  function getPublicUrl(path) {
    if (!path) return "";
    const { data } = supabase.storage.from("media").getPublicUrl(path);
    return data?.publicUrl || "";
  }

  const videoUrl = useMemo(() => {
    if (!movie) return "";
    if (movie.video_url) return movie.video_url; // Dropbox/external
    if (movie.video_path) return getPublicUrl(movie.video_path); // Supabase Storage
    return "";
  }, [movie]);

  const posterUrl = useMemo(() => {
    if (!movie) return "";
    if (movie.thumbnail_path) return getPublicUrl(movie.thumbnail_path);
    return "";
  }, [movie]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0b0c0c] text-white p-10">
        Loading...
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0b0c0c] text-white p-10">
        <p className="text-xl font-semibold mb-3">Error</p>
        <p className="text-gray-300 mb-6">{error}</p>
        <button
          onClick={loadMovie}
          className="bg-white text-black px-6 py-3 rounded font-semibold"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!movie) {
    return (
      <div className="min-h-screen bg-[#0b0c0c] text-white p-10">
        Movie not found.
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0b0c0c] text-white">
      {/* VIDEO PLAYER */}
      <div className="w-full bg-black">
        {videoUrl ? (
          <video
            key={movie.id}
            src={videoUrl}
            controls
            autoPlay
            className="w-full max-h-[70vh] bg-black object-contain"
            poster={posterUrl || undefined}
          />
        ) : (
          <div className="w-full h-[50vh] flex items-center justify-center text-gray-400">
            No video available.
          </div>
        )}
      </div>

      {/* MOVIE INFO */}
      <div className="px-8 py-6">
        <h1 className="text-3xl font-bold mb-2">{movie.title}</h1>

        {movie.duration ? (
          <p className="text-sm text-gray-400 mb-3">{movie.duration}</p>
        ) : null}

        {movie.description ? (
          <p className="text-gray-300 max-w-3xl">{movie.description}</p>
        ) : null}
      </div>
    </div>
  );
}
